import {
  IDS,
  adminEmployee,
  controlCard,
  documentStatuses,
  operationalRequest,
  organization,
  partnerOrganization,
  poaDraft,
  poaListItem,
  shipmentRegistry,
  shipmentReport,
  shipmentTask,
  staffEmployee,
} from './fixtures';
import {
  StampInfo,
  applyStamp,
  buildContractHtml,
  buildNdaXslt,
  decodeBase64,
  encodeBase64,
} from './documents';

const STATE_KEY = 'offline-mock-state';
const STATE_VERSION = 8;

type MockDocument = Record<string, unknown> & {
  id: string;
  title: string;
  number: string;
  statusId: string;
  tbs: string;
  tbd: Array<{ type: string; content: string }>;
  content?: string;
  signatures?: Array<Record<string, unknown>>;
  kind?: 'contract' | 'nda';
};

type OrgInvite = {
  id: string;
  status: 'sent' | 'approved' | 'rejected';
  joinDocumentId: string | null;
  createdAt: string;
  updatedAt: string;
  partyOrganization: typeof organization;
  partyEmployee: typeof adminEmployee;
  isIncoming: boolean;
};

type StaffInvite = {
  id: string;
  type: string;
  status: string;
  /** Входящее приглашение (нам) или исходящее (мы позвали) — списки в UI разные. */
  isIncoming: boolean;
  createdAt: string;
  updatedAt: string;
  employee: typeof adminEmployee;
  organization: typeof organization;
  inviteeEmployee: typeof staffEmployee;
  attributes: unknown[];
};

export type MockState = {
  version: number;
  organization: typeof organization;
  employees: Array<typeof adminEmployee>;
  partners: Array<typeof partnerOrganization>;
  documents: MockDocument[];
  orgInvites: OrgInvite[];
  staffInvites: StaffInvite[];
  paymentOptions: Array<Record<string, unknown>>;
  orgDocuments: Array<Record<string, unknown>>;
  poas: Array<Record<string, unknown>>;
  /** Записи, созданные через виджет-конструктор форм. */
  widgetRecords: Array<Record<string, unknown>>;
  fuel: {
    requests: Array<Record<string, unknown>>;
    tasks: Array<Record<string, unknown>>;
    cards: Array<Record<string, unknown>>;
    reports: Array<Record<string, unknown>>;
    registries: Array<Record<string, unknown>>;
  };
  assets: Array<Record<string, unknown>>;
};

/**
 * Сиды повторяют демо-набор из `docs/ktma-ldemo/data_assets.js`: расходомер с двумя
 * компонентами и техместом-родителем. Данные намеренно «настоящие» — так карточка
 * проверяется на длинных характеристиках, связях и незаполненных полях.
 *
 * `assetProperties` кладём объектом, как в контракте и макете, а не массивом пар:
 * это заодно проверяет нормализацию в `store/api/assets.ts`.
 */
function seedPassports(): Array<Record<string, unknown>> {
  const flowMeter = {
    id: 'passport-1',
    number: 1,
    name: 'Расходомер ВЗЛЕТ-ФБ',
    entityType: 'Изделие',
    type: 'КИПиА',
    class: 'Приборы / Измерительные / Расходомеры / Электромагнитные',
    productNumber: '85267-22-А',
    manufacturer: 'АО «ВЗЛЕТ»',
    factoryNumber: '2503625',
    inventoryNumber: 'IN.231870fb',
    manufactureDate: '01.2024',
    commissioningDate: '01.09.2025',
    locationId: '134.АО2.ШЛ2.24',
    locationCode: 'ШЛ-2-24',
    location: 'Управление, Стойка контроля ШЛ-2-24',
    status: 'DRAFT',
    statusId: IDS.STATUS_DRAFT,
    lifecycleStatus: 'Исправно',
    pzp: 'ПЗП-2085',
    did: 'did:i:izd:passport-1',
    vcId: 'urn:uuid:passport-1',
    assetProperties: {
      regTypeNumber: '85267-22',
      measurementRange: 'от 0,002 Qнаиб до Qнаиб, при скоростях потока до 10 м/с',
      accuracyClass: '± 1,0 % от 0,01 Qнаиб до Qнаиб',
      calibrationInterval: '72',
    },
    parentAssets: [
      {
        id: 'passport-3',
        name: 'Стойка измерительная',
        type: 'Техместо',
        relation: 'Технологическая',
        location: 'Управление, 24В Шкаф контроля ШЛ-2',
        status: 'DRAFT',
      },
    ],
    childAssets: [
      {
        id: 'passport-2',
        name: 'Преобразователь первичный ВЗЛЕТ ППР-001',
        type: 'КИПиА',
        factoryNumber: 'ППР-001',
        status: 'DRAFT',
      },
    ],
    eventLog: [
      {
        date: '01.09.2025',
        operation: 'Отгрузка',
        location: 'Терминал №2 поз.214-12',
        basis: 'Готовая продукция',
        responsible: 'ООО «Завод Взлет», начальник отдела, И.З. Гридовой',
      },
      {
        date: '04.09.2025',
        operation: 'Монтаж',
        location: 'Управление, Стойка контроля ШЛ-2-24',
        basis: 'Замена по заявке 09-2',
        responsible: 'ООО «Газпромнефть-Ямал», мастер КИП, В.Л. Астрикян',
      },
      {
        date: '27.08.2026',
        operation: 'Паспорт создан',
        responsible: 'Нефтесервисная компания, Петров Иван Иванович',
      },
    ],
    attachments: [
      {
        id: 'att-passport-1',
        passportId: 'passport-1',
        fileName: 'свидетельство-о-поверке.pdf',
        size: 128480,
        kind: 'Свидетельство о поверке',
        blobId: 'sha256-demo-1',
        signatures: [{ id: 'sig-1', fileName: 'signature.sig', size: 2048 }],
        signatureVerification: 'NOT_VERIFIED',
        vcId: 'urn:uuid:att-passport-1',
        createdAt: '2025-09-01T00:00:00Z',
      },
      {
        id: 'att-passport-2',
        passportId: 'passport-1',
        fileName: 'паспорт-изделия.pdf',
        size: 24480,
        kind: 'Паспорт изделия',
        blobId: 'sha256-demo-2',
        signatures: [],
        signatureVerification: 'NOT_VERIFIED',
        createdAt: '2024-02-01T00:00:00Z',
      },
    ],
    orgOwnerId: IDS.ORG_ID,
    createdAt: '2026-08-27T00:00:00Z',
  };

  const converter = {
    id: 'passport-2',
    number: 2,
    name: 'Преобразователь первичный ВЗЛЕТ ППР-001',
    entityType: 'Изделие',
    type: 'КИПиА',
    class: 'Приборы / Измерительные / Расходомеры / Электромагнитные',
    productNumber: 'ППР-001',
    manufacturer: 'АО «ВЗЛЕТ»',
    factoryNumber: 'ППР-001',
    manufactureDate: '01.2024',
    commissioningDate: '01.09.2025',
    locationCode: 'ШЛ-2-24',
    location: 'Управление, Стойка контроля ШЛ-2-24',
    status: 'DRAFT',
    statusId: IDS.STATUS_DRAFT,
    lifecycleStatus: 'Исправно',
    pzp: null,
    assetProperties: {
      name: 'Преобразователь расхода электромагнитный',
      identifier: 'ППР-001',
    },
    parentAssets: [
      {
        id: 'passport-1',
        name: 'Расходомер ВЗЛЕТ-ФБ',
        type: 'КИПиА',
        relation: 'Агрегат',
        location: 'Управление, Стойка контроля ШЛ-2-24',
        status: 'DRAFT',
      },
    ],
    childAssets: [],
    eventLog: [],
    orgOwnerId: IDS.ORG_ID,
    createdAt: '2026-08-27T00:00:00Z',
  };

  // У техместа нет изготовителя, типа изделия и исправности — контракт этих полей
  // для него не заполняет. Пустые значения проверяют, что карточка пишет «не заполнено».
  const place = {
    id: 'passport-3',
    number: 3,
    name: 'Стойка измерительная',
    entityType: 'Техместо',
    class: 'АСУТП',
    factoryNumber: null,
    inventoryNumber: 'СИ-2',
    locationCode: 'ШЛ-2-24',
    location: 'Управление, 24В Шкаф контроля ШЛ-2',
    status: 'DRAFT',
    statusId: IDS.STATUS_DRAFT,
    pzp: null,
    assetProperties: {},
    parentAssets: [],
    childAssets: [
      {
        id: 'passport-1',
        name: 'Расходомер ВЗЛЕТ-ФБ',
        type: 'КИПиА',
        factoryNumber: '2503625',
        status: 'DRAFT',
      },
    ],
    eventLog: [],
    orgOwnerId: IDS.ORG_ID,
    createdAt: '2026-08-27T00:00:00Z',
  };

  return [flowMeter, converter, place];
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function nowIso() {
  return new Date().toISOString();
}

function today() {
  return nowIso().slice(0, 10);
}

function newId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `mock-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function ownerStamp(signedAt = nowIso()): StampInfo {
  return {
    company: organization.name,
    lastName: adminEmployee.lastName,
    firstName: `${adminEmployee.firstName} ${adminEmployee.middleName}`,
    title: 'Директор',
    signedAt,
    serial: 'Test-77070838',
    validFrom: '2026-01-01',
    validUntil: '2027-01-01',
  };
}

function partnerStamp(signedAt = '2026-08-01T12:00:00Z'): StampInfo {
  return {
    company: partnerOrganization.name,
    lastName: 'Сидоров',
    firstName: 'Алексей Иванович',
    title: 'Генеральный директор',
    signedAt,
    serial: 'Test-50200528',
    validFrom: '2026-01-01',
    validUntil: '2027-01-01',
  };
}

function makeSignature(documentId: string, extra: Record<string, unknown> = {}) {
  return {
    id: newId(),
    documentID: documentId,
    employeeID: IDS.ADMIN_ID,
    organizationID: IDS.ORG_ID,
    committed: true,
    type: 'xmldsig',
    createdAt: nowIso(),
    updatedAt: nowIso(),
    revisions: [{ signatureID: 'rev-1', key: 'mock', hash: 'mock', body: 'mock-sign' }],
    ...extra,
  };
}

function attachMarkup(doc: MockDocument, markup: string, extraTbd: MockDocument['tbd'] = []) {
  doc.content = markup;
  doc.tbs = encodeBase64(markup);
  doc.tbd = [{ type: 'base', content: encodeBase64(markup) }, ...extraTbd];
}

function decodeMarkup(doc: MockDocument) {
  const raw = doc.tbd?.find((item) => item.type === 'base')?.content ?? doc.content ?? '';
  if (!raw) return '';
  if (raw.trim().startsWith('<')) return raw;
  try {
    return decodeBase64(raw);
  } catch {
    return raw;
  }
}

function seedContract(): MockDocument {
  const html = buildContractHtml({
    number: 'Д-1001',
    date: '01.08.2026',
    place: 'Москва',
    subject: 'поставку дизельного топлива в объёме, согласованном сторонами',
    owner: organization.name,
    partner: partnerOrganization.name,
    stamps: [ownerStamp('2026-08-01T10:00:00Z'), partnerStamp('2026-08-01T12:00:00Z')],
  });

  const doc: MockDocument = {
    id: IDS.DOC_ID,
    kind: 'contract',
    title: 'Договор поставки топлива',
    number: 'Д-1001',
    typeId: 'supply',
    docDate: '2026-08-01',
    version: 1,
    statusId: IDS.STATUS_SIGNED,
    createdAt: '2026-08-01T00:00:00Z',
    updatedAt: '2026-08-01T00:00:00Z',
    validFrom: '2026-08-01',
    validTo: '2027-08-01',
    tbs: encodeBase64(html),
    tbd: [{ type: 'base', content: encodeBase64(html) }],
    content: html,
    contractors: [{ id: IDS.PARTNER_ORG_ID, name: partnerOrganization.name }],
    template: { id: 'tpl-supply' },
    orgOwner: {
      id: IDS.ORG_ID,
      name: organization.name,
      status: '1',
      type: '1',
      countryCode: 'ru',
      createdAt: organization.createdAt,
      updatedAt: organization.updatedAt,
    },
    signatures: [
      makeSignature(IDS.DOC_ID, { createdAt: '2026-08-01T10:00:00Z' }),
      makeSignature(IDS.DOC_ID, {
        id: 'sig-partner',
        employeeID: IDS.STAFF_ID,
        organizationID: IDS.PARTNER_ORG_ID,
        createdAt: '2026-08-01T12:00:00Z',
      }),
    ],
  };
  return doc;
}

function seedNda(): MockDocument {
  const xml = buildNdaXslt({
    number: 'НДА-1001',
    date: '01.08.2026',
    location: 'г. Москва',
    durationYears: 3,
    owner: partnerOrganization.name,
    partner: organization.name,
    stamps: [partnerStamp('2026-08-02T09:00:00Z')],
  });

  return {
    id: IDS.NDA_DOC_ID,
    kind: 'nda',
    title: 'Соглашение о неразглашении конфиденциальной информации',
    number: 'НДА-1001',
    typeId: 'nda',
    docDate: '2026-08-01',
    version: 1,
    statusId: IDS.STATUS_APPROVED,
    createdAt: '2026-08-02T00:00:00Z',
    updatedAt: '2026-08-02T00:00:00Z',
    validFrom: '2026-08-02',
    validTo: '2029-08-02',
    tbs: encodeBase64(xml),
    tbd: [{ type: 'base', content: xml }],
    content: xml,
    contractors: [{ id: IDS.PARTNER_ORG_ID, name: partnerOrganization.name }],
    template: { id: 'tpl-nda' },
    orgOwner: {
      id: IDS.PARTNER_ORG_ID,
      name: partnerOrganization.name,
      status: '1',
      type: '1',
      countryCode: 'ru',
      createdAt: partnerOrganization.createdAt,
      updatedAt: partnerOrganization.updatedAt,
    },
    signatures: [
      makeSignature(IDS.NDA_DOC_ID, {
        employeeID: IDS.STAFF_ID,
        organizationID: IDS.PARTNER_ORG_ID,
        createdAt: '2026-08-02T09:00:00Z',
      }),
    ],
  };
}

function seedState(): MockState {
  return {
    version: STATE_VERSION,
    organization: clone(organization),
    employees: [clone(adminEmployee), clone(staffEmployee)],
    partners: [clone(partnerOrganization)],
    documents: [seedContract(), seedNda()],
    orgInvites: [
      {
        id: IDS.INCOMING_INVITE_ID,
        status: 'sent',
        joinDocumentId: IDS.NDA_DOC_ID,
        createdAt: '2026-08-02T00:00:00Z',
        updatedAt: '2026-08-02T00:00:00Z',
        partyOrganization: clone(partnerOrganization),
        partyEmployee: clone(staffEmployee),
        isIncoming: true,
      },
      {
        id: IDS.INVITE_ID,
        status: 'sent',
        joinDocumentId: null,
        createdAt: '2026-08-01T00:00:00Z',
        updatedAt: '2026-08-01T00:00:00Z',
        partyOrganization: clone(partnerOrganization),
        partyEmployee: clone(staffEmployee),
        isIncoming: false,
      },
    ],
    staffInvites: [
      {
        id: IDS.STAFF_INVITE_ID,
        type: 'employee_participation_invite',
        status: 'sent',
        isIncoming: true,
        createdAt: '2026-08-01T00:00:00Z',
        updatedAt: '2026-08-01T00:00:00Z',
        employee: clone(adminEmployee),
        organization: clone(organization),
        inviteeEmployee: clone(staffEmployee),
        attributes: [],
      },
    ],
    paymentOptions: [
      {
        id: 'pay-aaa',
        name: 'Авансовый счёт',
        isDefault: false,
        accountNumber: '40702810100000000999',
        corrAccountNumber: '30101810400000000225',
        corrNumber: '30101810400000000225',
        bic: '044525999',
        currency: 'RUB',
        docId: '000000000',
        bank: {
          id: 'bank-2',
          name: 'АО Альфа-Банк',
          status: 5,
          type: 6,
          countryCode: 'ru',
          createdAt: '2026-01-01T00:00:00Z',
          updatedAt: '2026-01-01T00:00:00Z',
        },
      },
      {
        id: 'pay-1',
        name: 'Основной счёт',
        isDefault: true,
        accountNumber: '40702810100000000001',
        corrAccountNumber: '30101810400000000225',
        corrNumber: '30101810400000000225',
        bic: '044525225',
        currency: 'RUB',
        docId: '000000000',
        bank: {
          id: 'bank-1',
          name: 'ПАО Сбербанк',
          status: 5,
          type: 6,
          countryCode: 'ru',
          createdAt: '2026-01-01T00:00:00Z',
          updatedAt: '2026-01-01T00:00:00Z',
        },
      },
    ],
    orgDocuments: [
      {
        id: 'odoc-1',
        createdAt: '2026-08-01T00:00:00Z',
        updatedAt: '2026-08-01T00:00:00Z',
        name: 'Устав',
        extension: 'pdf',
        size: 128000,
      },
    ],
    poas: [clone(poaListItem)],
    widgetRecords: [],
    fuel: {
      requests: [operationalRequest()],
      tasks: [shipmentTask()],
      cards: [controlCard()],
      reports: [shipmentReport()],
      registries: [shipmentRegistry()],
    },
    assets: seedPassports(),
  };
}

let memory: MockState | null = null;

function persist(state: MockState) {
  memory = state;
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    // quota / private mode — keep the in-memory copy
  }
}

export function resetMockState() {
  persist(seedState());
}

export function getState(): MockState {
  if (memory && memory.version === STATE_VERSION) {
    return memory;
  }

  try {
    const raw = localStorage.getItem(STATE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as MockState;
      if (parsed?.version === STATE_VERSION && Array.isArray(parsed.documents)) {
        memory = parsed;
        return parsed;
      }
    }
  } catch {
    // ignore broken storage
  }

  const seeded = seedState();
  persist(seeded);
  return seeded;
}

function mutate(update: (state: MockState) => void): MockState {
  const state = getState();
  update(state);
  persist(state);
  return state;
}

function findDocument(id: string) {
  return getState().documents.find((doc) => doc.id === id);
}

export function listDocuments() {
  return getState().documents;
}

export function getDocumentsByIds(ids: string[]) {
  if (!ids.length) {
    return listDocuments();
  }
  return ids.map((id) => findDocument(id)).filter(Boolean) as MockDocument[];
}

/**
 * Каждое приложение присылает поля своей схемы: у топлива это `contractNumber`,
 * у КТМ-А — `DocumentNumber` и т.д. Мок читает оба набора, иначе договор Активов
 * выглядел бы как поставка нефтепродуктов с чужим номером.
 */
export function createContract(
  input: Record<string, unknown> = {},
  meta: { title?: string; typeId?: string; templateId?: string } = {},
) {
  const id = newId();
  const number = String(
    input.contractNumber ?? input.DocumentNumber ?? input.number ?? `Д-${Date.now() % 10000}`,
  );
  const date = String(input.conclusionDate ?? input.StartingDate ?? input.date ?? today());
  const place = String(
    input.conclusionPlace ?? input.DraftingPlace ?? input.location ?? 'Москва',
  );
  const subject = String(input.subject ?? meta.title ?? 'поставку нефтепродуктов');
  const html = buildContractHtml({
    number,
    date,
    place,
    subject,
    title: meta.title,
    owner: getState().organization.name,
    partner: partnerOrganization.name,
  });

  const doc: MockDocument = {
    id,
    kind: 'contract',
    title: meta.title ?? 'Договор поставки топлива',
    number,
    typeId: meta.typeId ?? 'supply',
    docDate: date,
    version: 0,
    statusId: IDS.STATUS_DRAFT,
    createdAt: nowIso(),
    updatedAt: nowIso(),
    validFrom: date,
    validTo: String(input.DocumentValidityPeriod ?? '2027-08-01'),
    tbs: encodeBase64(html),
    tbd: [{ type: 'base', content: encodeBase64(html) }],
    content: html,
    contractors: [{ id: IDS.PARTNER_ORG_ID, name: partnerOrganization.name }],
    // Карточка договора читает поля формы из `parameters` — без них «Место заключения»
    // и «Предмет» на созданном документе оставались пустыми.
    parameters: [
      { name: 'DocumentNumber', value: number },
      { name: 'DraftingPlace', value: place },
      { name: 'DocumentSubject', value: subject },
    ],
    template: { id: meta.templateId ?? 'tpl-supply' },
    orgOwner: {
      id: IDS.ORG_ID,
      name: getState().organization.name,
      status: '1',
      type: '1',
      countryCode: 'ru',
      createdAt: organization.createdAt,
      updatedAt: nowIso(),
    },
    signatures: [],
  };

  mutate((state) => {
    state.documents.unshift(doc);
  });
  return doc;
}

export function signDocument(id: string, signature?: string) {
  let updated: MockDocument | undefined;
  mutate((state) => {
    const doc = state.documents.find((item) => item.id === id);
    if (!doc) return;
    const stamped = applyStamp(decodeMarkup(doc), ownerStamp());
    attachMarkup(
      doc,
      stamped,
      signature ? [{ type: 'signature', content: String(signature) }] : [],
    );
    doc.statusId = IDS.STATUS_SIGNED;
    doc.version = Number(doc.version ?? 0) + 1;
    doc.updatedAt = nowIso();
    doc.signatures = [...(doc.signatures ?? []), makeSignature(doc.id)];
    updated = doc;
  });
  return updated;
}

export function startOrgInvite(input: {
  partnerOrganizationId?: string;
  joinDocument?: {
    number?: string;
    date?: string;
    location?: string;
    durationYears?: number;
  } | null;
}) {
  const inviteId = newId();
  const join = input.joinDocument;
  let joinDocumentContent: string | undefined;
  let joinDocumentId: string | null = null;

  if (join) {
    const xml = buildNdaXslt({
      number: String(join.number ?? `НДА-${Date.now() % 10000}`),
      date: String(join.date ?? today()),
      location: String(join.location ?? 'г. Москва'),
      durationYears: Number(join.durationYears ?? 3),
      owner: getState().organization.name,
      partner: partnerOrganization.name,
    });
    joinDocumentContent = encodeBase64(xml);
    const doc: MockDocument = {
      id: newId(),
      kind: 'nda',
      title: 'Соглашение о неразглашении конфиденциальной информации',
      number: String(join.number ?? 'НДА'),
      typeId: 'nda',
      docDate: String(join.date ?? today()),
      version: 0,
      statusId: IDS.STATUS_DRAFT,
      createdAt: nowIso(),
      updatedAt: nowIso(),
      validFrom: today(),
      validTo: '2029-08-01',
      tbs: encodeBase64(xml),
      tbd: [{ type: 'base', content: xml }],
      content: xml,
      contractors: [{ id: IDS.PARTNER_ORG_ID, name: partnerOrganization.name }],
      template: { id: 'tpl-nda' },
      orgOwner: {
        id: IDS.ORG_ID,
        name: getState().organization.name,
        status: '1',
        type: '1',
        countryCode: 'ru',
        createdAt: organization.createdAt,
        updatedAt: nowIso(),
      },
      signatures: [],
    };
    joinDocumentId = doc.id;
    mutate((state) => {
      state.documents.unshift(doc);
    });
  }

  mutate((state) => {
    state.orgInvites.unshift({
      id: inviteId,
      status: 'sent',
      joinDocumentId,
      createdAt: nowIso(),
      updatedAt: nowIso(),
      partyOrganization: clone(partnerOrganization),
      partyEmployee: clone(adminEmployee),
      isIncoming: false,
    });
  });

  return { inviteId, tbs: 'tbs-mock', joinDocumentContent };
}

export function finishOrgInvite(input: { inviteId?: string; signature?: string }) {
  mutate((state) => {
    const invite = state.orgInvites.find((item) => item.id === input.inviteId);
    if (!invite) return;
    invite.status = 'sent';
    invite.updatedAt = nowIso();
    if (!invite.joinDocumentId || !input.signature) return;
    const doc = state.documents.find((item) => item.id === invite.joinDocumentId);
    if (!doc) return;
    let markup = decodeMarkup(doc);
    try {
      const decoded = decodeBase64(String(input.signature));
      if (decoded.includes('</body>') || decoded.includes('<?xml')) {
        markup = decoded;
      }
    } catch {
      markup = applyStamp(markup, ownerStamp());
    }
    if (!markup.includes('3b4395')) {
      markup = applyStamp(markup, ownerStamp());
    }
    attachMarkup(doc, markup, [{ type: 'signature', content: String(input.signature) }]);
    if (doc.kind === 'nda') {
      doc.tbd = [{ type: 'base', content: markup }, { type: 'signature', content: String(input.signature) }];
      doc.content = markup;
    }
    doc.statusId = IDS.STATUS_APPROVED;
    doc.signatures = [...(doc.signatures ?? []), makeSignature(doc.id)];
    doc.updatedAt = nowIso();
  });
  return { inviteId: input.inviteId ?? IDS.INVITE_ID };
}

export function confirmOrgInvite(input: { inviteId?: string; signature?: string }) {
  mutate((state) => {
    const invite = state.orgInvites.find((item) => item.id === input.inviteId);
    if (!invite) return;
    invite.status = 'approved';
    invite.updatedAt = nowIso();
    if (invite.joinDocumentId) {
      const doc = state.documents.find((item) => item.id === invite.joinDocumentId);
      if (doc) {
        const stamped = applyStamp(decodeMarkup(doc), ownerStamp());
        doc.tbd = [{ type: 'base', content: stamped }];
        doc.content = stamped;
        doc.tbs = encodeBase64(stamped);
        doc.statusId = IDS.STATUS_SIGNED;
        doc.signatures = [...(doc.signatures ?? []), makeSignature(doc.id)];
        doc.updatedAt = nowIso();
      }
    }
    if (!state.partners.some((partner) => partner.id === invite.partyOrganization.id)) {
      state.partners.push(clone(invite.partyOrganization));
    }
  });
  return true;
}

export function rejectOrgInvite(inviteId?: string) {
  mutate((state) => {
    const invite = state.orgInvites.find((item) => item.id === inviteId);
    if (invite) {
      invite.status = 'rejected';
      invite.updatedAt = nowIso();
    }
  });
  return true;
}

export function listOrgInvites(isIncoming: boolean) {
  return getState().orgInvites.filter((invite) => invite.isIncoming === isIncoming);
}

/** Данные приглашаемого сотрудника из формы: мок должен показывать именно их. */
export type StaffInviteInput = {
  FirstName?: string;
  LastName?: string;
  MiddleName?: string;
  INN?: string;
  SNILS?: string;
  participantEmail?: string[];
};

export function createStaffInvite(participant?: StaffInviteInput, role?: string) {
  const invitee = clone(staffEmployee);
  if (participant) {
    invitee.firstName = participant.FirstName ?? invitee.firstName;
    invitee.lastName = participant.LastName ?? invitee.lastName;
    invitee.middleName = participant.MiddleName ?? invitee.middleName;
    invitee.inn = participant.INN ?? invitee.inn;
    invitee.snils = participant.SNILS ?? invitee.snils;
    invitee.email = participant.participantEmail?.[0] ?? invitee.email;
    invitee.role = role ?? invitee.role;
  }

  const invite: StaffInvite = {
    id: newId(),
    type: 'employee_participation_invite',
    status: 'sent',
    isIncoming: false,
    createdAt: nowIso(),
    updatedAt: nowIso(),
    employee: clone(adminEmployee),
    organization: clone(getState().organization),
    inviteeEmployee: invitee,
    attributes: [],
  };
  mutate((state) => {
    state.staffInvites.unshift(invite);
  });
  return invite.id;
}

/**
 * Принимаем именно то приглашение, id которого прислал фронт. Отказ уходит той же
 * мутацией с `isAccepted: false`, поэтому статус выбирается флагом.
 */
export function acceptStaffInvite(invitationId?: string, isAccepted = true) {
  mutate((state) => {
    const invite = invitationId
      ? state.staffInvites.find((item) => item.id === invitationId)
      : state.staffInvites.find((item) => item.status === 'sent');
    if (invite) {
      invite.status = isAccepted ? 'approved' : 'rejected';
      invite.updatedAt = nowIso();
    }
  });
  return true;
}

export function listStaffInvites(isIncoming?: boolean) {
  const invites = getState().staffInvites;

  return isIncoming === undefined
    ? invites
    : invites.filter((invite) => invite.isIncoming === isIncoming);
}

export function updateOrganization(input: Record<string, unknown> = {}) {
  mutate((state) => {
    if (typeof input.name === 'string' && input.name) {
      state.organization.name = input.name;
    }
    if (Array.isArray(input.attributes)) {
      // Форма присылает только редактируемые атрибуты, поэтому мержим:
      // иначе ИНН и ОГРН пропадали из карточки после сохранения.
      const incoming = input.attributes as typeof organization.attributes;
      const merged = [...state.organization.attributes];
      incoming.forEach((attribute) => {
        const index = merged.findIndex((item) => item.name === attribute.name);
        if (index === -1) {
          merged.push(attribute);
        } else {
          merged[index] = attribute;
        }
      });
      state.organization.attributes = merged;
    }
    state.organization.updatedAt = nowIso();
  });
  return getState().organization;
}

const MOCK_BANKS: Record<string, string> = {
  'bank-1': 'ПАО Сбербанк',
  'bank-2': 'АО Альфа-Банк',
};

export function addPaymentOption(input: Record<string, unknown> = {}) {
  const bankId = typeof input.bankId === 'string' ? input.bankId : '';
  const incoming: Record<string, unknown> = {
    ...input,
    corrNumber: input.corrAccountNumber ?? input.corrNumber,
    bank:
      input.bank ??
      (bankId
        ? {
            id: bankId,
            name: MOCK_BANKS[bankId] ?? bankId,
            status: 5,
            type: 6,
            countryCode: 'ru',
            createdAt: nowIso(),
            updatedAt: nowIso(),
          }
        : undefined),
  };

  let saved: Record<string, unknown> = { id: newId(), ...incoming };
  mutate((state) => {
    const id = typeof input.id === 'string' ? input.id : undefined;
    const index = id
      ? state.paymentOptions.findIndex((item) => item.id === id)
      : incoming.isDefault
        ? state.paymentOptions.findIndex((item) => item.isDefault)
        : -1;

    if (index >= 0) {
      saved = { ...state.paymentOptions[index], ...incoming, id: state.paymentOptions[index].id };
      state.paymentOptions[index] = saved;
      return;
    }

    state.paymentOptions.push(saved);
  });
  return saved;
}

export function addOrgDocument(name = 'Документ') {
  const doc = {
    id: newId(),
    createdAt: nowIso(),
    updatedAt: nowIso(),
    name,
    extension: 'pdf',
    size: 1024,
  };
  mutate((state) => {
    state.orgDocuments.unshift(doc);
  });
  return doc;
}

export function removeOrgDocument(id?: string) {
  mutate((state) => {
    state.orgDocuments = state.orgDocuments.filter((item) => item.id !== id);
  });
  return true;
}

export function addPoa() {
  const id = newId();
  const draft = { ...clone(poaDraft), id, number: `МЧД-${Date.now() % 10000}`, status: 'draft' };
  mutate((state) => {
    state.poas.unshift({ poaDraft: draft, authority: draft.authority, representative: draft.representative });
  });
  return id;
}

/** Запись, созданная через конструктор форм, должна появляться в списке виджета. */
export function addWidgetRecord(input: Record<string, unknown> = {}) {
  const record = {
    id: newId(),
    orgOwnerId: getState().organization.id,
    clickOperation: 'view',
    data: {
      number: String(input.contractNumber ?? input.number ?? ''),
      agreement: String(input.subject ?? input.agreement ?? ''),
      status: 'Черновик',
      statusId: IDS.STATUS_DRAFT,
    },
  };
  mutate((state) => {
    state.widgetRecords.unshift(record);
  });
  return record;
}

export function listWidgetRecords() {
  return getState().widgetRecords;
}

export function cancelWidgetRecord(id: string) {
  mutate((state) => {
    const record = state.widgetRecords.find((item) => item.id === id);
    if (record) {
      (record.data as Record<string, unknown>).status = 'Отозван';
    }
  });
  return true;
}

export function updateFuelItemStatus(
  kind: keyof MockState['fuel'],
  id: string,
  statusId: string,
) {
  mutate((state) => {
    const item = state.fuel[kind].find((entry) => entry.id === id);
    if (item) {
      item.statusId = statusId;
      item.updatedAt = nowIso();
    }
  });
}

export function listPassports(filters: {
  search?: string;
  entityType?: string;
  status?: string;
  pzp?: string;
} = {}) {
  return getState().assets.filter((item) => {
    if (filters.entityType && filters.entityType !== 'all' && item.entityType !== filters.entityType) {
      return false;
    }
    if (filters.status && filters.status !== 'all' && item.status !== filters.status) {
      return false;
    }
    if (filters.pzp && String(item.pzp ?? '') !== filters.pzp) {
      return false;
    }
    if (filters.search) {
      // Поиск на бэке идёт по идентификационным полям — повторяем тот же набор.
      const hay = [item.name, item.factoryNumber, item.inventoryNumber, item.manufacturer, item.productNumber]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!hay.includes(filters.search.toLowerCase())) return false;
    }
    return true;
  });
}

export function getPassport(id: string) {
  return getState().assets.find((item) => item.id === id) ?? null;
}

export function listPassportAttachments(id: string) {
  const attachments = getPassport(id)?.attachments;
  return Array.isArray(attachments) ? attachments : [];
}

export function addPassport(input: Record<string, unknown>) {
  const factoryNumber = String(input.factoryNumber ?? '');
  if (factoryNumber && getState().assets.some((item) => item.factoryNumber === factoryNumber)) {
    const error = { code: 409, message: 'Заводской номер уже существует' };
    throw error;
  }
  // `CreateDigitalPassportRequest` не содержит исправности и связей — свежий паспорт
  // приходит из DRAFT только с тем, что отправила форма.
  const item = {
    id: newId(),
    number: getState().assets.length + 1,
    name: input.name,
    entityType: input.entityType,
    type: input.type,
    manufacturer: input.manufacturer,
    factoryNumber: factoryNumber || null,
    inventoryNumber: input.inventoryNumber,
    locationId: input.locationId,
    status: 'DRAFT',
    statusId: IDS.STATUS_DRAFT,
    pzp: null,
    parentAssets: [],
    childAssets: [],
    assetProperties: {},
    eventLog: [{ date: today(), operation: 'Паспорт создан' }],
    attachments: [],
    orgOwnerId: IDS.ORG_ID,
    createdAt: nowIso(),
  };
  mutate((state) => {
    state.assets.unshift(item);
  });
  return item;
}

export function addFuelItem(
  kind: keyof MockState['fuel'],
  input: Record<string, unknown> = {},
) {
  // Бэкенд достраивает номер, дату заявки, владельца и название соглашения —
  // без этого созданная строка выглядит в списке пустой.
  const number = getState().fuel[kind].length + 1;
  const item = {
    id: newId(),
    version: 1,
    number,
    statusId: IDS.STATUS_DRAFT,
    orgOwnerId: getState().organization.id,
    agreementName: 'Доп. соглашение №1',
    requestDate: today(),
    employeeOwner: IDS.ADMIN_ID,
    employeeReceiving: IDS.STAFF_ID,
    deliveryBasis: 'FCA',
    supplyAddress: 'Москва',
    createdAt: nowIso(),
    updatedAt: nowIso(),
    ...input,
  };
  mutate((state) => {
    state.fuel[kind].unshift(item);
  });
  return item;
}

export function documentStatusesList() {
  return documentStatuses;
}
